<template>
  <div class="api-key-container">
    <el-card class="api-key-card">
      <h2 class="api-key-title">API Key 管理</h2>
      <el-row v-if="loading" justify="center" style="margin: 32px 0;">
        <el-spinner />
      </el-row>
      <el-row v-else>
        <el-col :span="24">
          <el-alert v-if="error" :title="error" type="error" show-icon :closable="false" style="margin-bottom:16px" />
          
          <!-- 如果没有API Key，显示生成按钮 -->
          <div v-if="!apiKey">
            <el-alert 
              title="您还没有API Key，请先生成一个" 
              type="warning" 
              show-icon 
              :closable="false" 
              style="margin-bottom: 16px;" 
            />
            <el-button type="primary" @click="onCreate" :loading="creating" style="margin-bottom: 24px;">
              生成API Key
            </el-button>
          </div>
          
          <!-- 如果有API Key，显示API Key信息 -->
          <div v-else>
            <el-alert 
              title="您已拥有API Key，可以直接使用" 
              type="success" 
              show-icon 
              :closable="false" 
              style="margin-bottom: 16px;" 
            />
            <div class="show-block">
              <div class="block-label">API Key：</div>
              <el-input v-model="apiKey" readonly size="medium" style="width: 80%; margin-right: 8px;" />
              <el-button type="text" size="mini" @click="copy(apiKey)">复制</el-button>
            </div>
            <div class="show-block">
              <div class="block-label">Search接口示例：</div>
              <el-input v-model="searchUrl" readonly size="medium" style="width: 80%; margin-right: 8px;" />
              <el-button type="text" size="mini" @click="copy(searchUrl)">复制</el-button>
            </div>
            <div style="margin-top: 16px;">
              <el-alert 
                title="API Key安全提示" 
                type="warning" 
                show-icon 
                :closable="false" 
                style="margin-top: 8px;" 
              >
                <div>
                  <p>为了保障API Key的安全使用，系统有以下限制：</p>
                  <ul>
                    <li>每分钟最多请求60次，超出将返回429错误</li>
                    <li>60秒内请求超过300次，API Key将被封禁4小时</li>
                    <li>5分钟内使用超过4个不同IP，API Key将被封禁4小时</li>
                  </ul>
                  <p>请妥善保管您的API Key，避免泄露或滥用。</p>
                </div>
              </el-alert>
            </div>
          </div>
        </el-col>
      </el-row>
    </el-card>
  </div>
</template>

<script>
export default {
  name: 'ApiKey',
  data() {
    return {
      apiKey: '',
      loading: false,
      creating: false,
      error: ''
    }
  },
  async mounted() {
    await this.fetchApiKey();
  },
  computed: {
    searchUrl() {
      const base = window.location.origin;
      return `${base}/api/search?api_key=${this.apiKey}&q=扫毒风暴&pan=quark,baidu`;
    }
  },
  methods: {
    async fetchApiKey() {
      this.loading = true;
      this.error = '';
      try {
        // 从用户信息接口获取API Key
        const res = await this.$axios.get('/user/info');
        if (res && res.code === 200 && res.data && res.data.api_key) {
          this.apiKey = res.data.api_key;
          localStorage.setItem('api_key', res.data.api_key);
        } else {
          // 如果没有API Key，尝试从localStorage获取
          const cachedApiKey = localStorage.getItem('api_key');
          if (cachedApiKey) {
            this.apiKey = cachedApiKey;
          }
        }
      } catch (e) {
        console.error('获取API Key失败:', e);
        // 如果接口失败，尝试从localStorage获取
        const cachedApiKey = localStorage.getItem('api_key');
        if (cachedApiKey) {
          this.apiKey = cachedApiKey;
        }
      } finally {
        this.loading = false;
      }
    },
    async onCreate() {
      this.creating = true;
      this.error = '';
      try {
        // 生成新的API Key
        const res = await this.$axios.post('/user/api_key', {});
        if (res && res.code === 200) {
          this.apiKey = res.api_key;
          localStorage.setItem('api_key', res.api_key);
          this.$message.success('API Key生成成功');
        } else {
          this.error = res.message || '生成API Key失败';
        }
      } catch (e) {
        this.error = e.response?.data?.message || '网络错误';
      } finally {
        this.creating = false;
      }
    },
    copy(val) {
      if (!val) return;
      if (navigator.clipboard) {
        navigator.clipboard.writeText(val).then(() => {
          this.$message.success('已复制到剪贴板');
        });
      } else {
        const input = document.createElement('input');
        input.value = val;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
        this.$message.success('已复制到剪贴板');
      }
    }
  }
}
</script>

<style scoped>
.api-key-container {
  max-width: 700px;
  margin: 32px auto;
}
.api-key-card {
  padding: 24px 24px 8px 24px;
}
.api-key-title {
  text-align: center;
  margin-bottom: 24px;
  font-weight: 500;
}
.show-block {
  display: flex;
  align-items: center;
  margin-bottom: 18px;
}
.block-label {
  min-width: 110px;
  font-weight: 500;
  color: #666;
}
@media (max-width: 768px) {
  .api-key-container {
    padding: 4px;
  }
  .api-key-title {
    font-size: 1.1rem;
    margin-bottom: 12px;
  }
  .api-key-card {
    padding: 10px 4px 4px 4px;
  }
  .show-block {
    flex-direction: column;
    align-items: stretch;
    margin-bottom: 12px;
  }
  .block-label {
    margin-bottom: 4px;
    min-width: 0;
  }
  .el-input,
  .el-button {
    width: 100% !important;
    min-width: 0 !important;
    box-sizing: border-box;
    margin-bottom: 4px;
  }
}
</style> 